package com.trevorwiebe.timesheet.admin.reporting.domain

import com.trevorwiebe.timesheet.mileage.domain.model.unitNumberOrder
import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.loads.domain.TruckMatch
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.resolvedTruckId
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import kotlin.math.roundToInt

/** Money values are whole cents. */
data class TruckRevenueTotal(
    val truckId: String,
    val unitNumber: String,
    val loadCount: Int,
    val revenue: Int,
    val revenueExFuel: Int,
    val miles: Long,
) {
    val revenuePerMileFormatted: String get() = perMile(revenue, miles)
    val revenueExFuelPerMileFormatted: String get() = perMile(revenueExFuel, miles)
    val revenueFormatted: String get() = "$" + revenue.formatCentsAsDollars()
}

data class RevenuePerMileReport(
    val rows: List<TruckRevenueTotal> = emptyList(),
    val unassignedLoadCount: Int = 0,
    val unassignedRevenue: Int = 0,
    // Loads with no rate, or a rate whose amounts were never filled in (e.g. 0 tons).
    val zeroRevenueLoadCount: Int = 0,
) {
    val totalRevenue: Int get() = rows.sumOf { it.revenue }
    val totalRevenueExFuel: Int get() = rows.sumOf { it.revenueExFuel }
    val totalMiles: Long get() = rows.sumOf { it.miles }
    val totalRevenueFormatted: String get() = "$" + totalRevenue.formatCentsAsDollars()
    val totalRevenuePerMileFormatted: String get() = perMile(totalRevenue, totalMiles)
    val totalRevenueExFuelPerMileFormatted: String get() = perMile(totalRevenueExFuel, totalMiles)
    val unassignedRevenueFormatted: String get() = "$" + unassignedRevenue.formatCentsAsDollars()

    /** Tapping this notice should open the Loads Without Truck report. */
    val unassignedNotice: String?
        get() = if (unassignedLoadCount == 0) null else
            "${loadsLabel(unassignedLoadCount)} (${unassignedRevenueFormatted}) couldn't be matched to a " +
                "truck and aren't in these totals. Review them in Loads Without Truck."

    val zeroRevenueNotice: String?
        get() = if (zeroRevenueLoadCount == 0) null else
            "${loadsLabel(zeroRevenueLoadCount)} have no rate or a $0 rate, so revenue per mile reads low."
}

private fun loadsLabel(count: Int) = if (count == 1) "1 load" else "$count loads"

/**
 * Revenue per mile for each truck. Every load in [loads] is credited to the truck in [matches];
 * loads without a single resolved truck are totalled as unassigned rather than guessed at.
 * Miles come from the General Mileage calculation, so both reports agree. Trucks that drove
 * but earned nothing still get a row — empty miles are worth seeing.
 */
fun calculateRevenuePerMileReport(
    loads: List<Load>,
    matches: Map<String, TruckMatch>,
    mileage: List<TruckMileageTotal>,
    equipment: List<Equipment>,
): RevenuePerMileReport {
    val byTruck = mutableMapOf<String, MutableList<Load>>()
    var unassignedCount = 0
    var unassignedRevenue = 0
    for (load in loads) {
        val truckId = matches[load.id]?.resolvedTruckId
        if (truckId == null) {
            unassignedCount++
            unassignedRevenue += load.rate?.total ?: 0
        } else {
            byTruck.getOrPut(truckId) { mutableListOf() }.add(load)
        }
    }

    val milesByTruck = mileage.associate { it.truckId to it.totalMiles }
    val rows = (byTruck.keys + milesByTruck.keys).map { truckId ->
        val truckLoads = byTruck[truckId].orEmpty()
        TruckRevenueTotal(
            truckId = truckId,
            unitNumber = equipment.firstOrNull { it.id == truckId }?.unitNumber ?: truckId,
            loadCount = truckLoads.size,
            revenue = truckLoads.sumOf { it.rate?.total ?: 0 },
            revenueExFuel = truckLoads.sumOf { load ->
                load.rate?.let { it.total - it.fuelSurchargeAmount } ?: 0
            },
            miles = milesByTruck[truckId] ?: 0L,
        )
    }.sortedWith(compareBy(unitNumberOrder) { it.unitNumber })

    return RevenuePerMileReport(
        rows = rows,
        unassignedLoadCount = unassignedCount,
        unassignedRevenue = unassignedRevenue,
        zeroRevenueLoadCount = loads.count { (it.rate?.total ?: 0) == 0 },
    )
}

private fun perMile(cents: Int, miles: Long): String =
    if (miles <= 0L) "—" else "$" + (cents.toDouble() / miles).roundToInt().formatCentsAsDollars()
